// src\components\VideoEngine\Templates\TikTok_Portrait\Composition.js

import React, { useEffect, useState } from "react";
import { AbsoluteFill, Sequence, continueRender, delayRender, Audio, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { TransitionWrapper } from "../../Common/TransitionWrapper";
// --- LOCAL SPOKE IMPORTS ---
import { IntroSlide } from "./Sequences/Intro";
import { RankingSlide } from "./Sequences/RankingSlide";
import { OutroSlide } from "./Sequences/Outro";

const loadFonts = () => {
  const handle = delayRender("Loading Google Fonts");
  const fontLink = document.createElement("link");
  fontLink.href = "https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=Fredoka:wght@300..700&family=Outfit:wght@400;700;900&display=swap";
  fontLink.rel = "stylesheet";
  document.head.appendChild(fontLink);
  fontLink.onload = async () => {
    try {
      await document.fonts.load("400 12px 'Outfit'");
      await document.fonts.load("700 12px 'Outfit'");
      await document.fonts.load("900 12px 'Outfit'");
      await document.fonts.load("400 12px 'Bebas Neue'");
      // INJECT NEW FONT BLOCKER HERE:
      await document.fonts.load("400 12px 'Anton'");
      // INJECTED: Explicitly load standard Fredoka weight
      await document.fonts.load("400 12px 'Fredoka'");
      await document.fonts.ready;
      continueRender(handle);
    } catch (err) {
      console.warn("System: Font loading forced exit:", err);
      continueRender(handle);
    }
  };
};

const FilmGrain = ({ opacity = 0.05 }) => (
  <AbsoluteFill style={{ pointerEvents: "none", opacity, zIndex: 99 }}>
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <filter id="noiseFilter">
        <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
      </filter>
      <rect width="100%" height="100%" filter="url(#noiseFilter)" />
    </svg>
  </AbsoluteFill>
);

const FALLBACK_ASSETS = {
  'food': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Ffood.mp4?alt=media&token=9d93d720-dd76-491a-9531-2f429dbeda0e',
  'money': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fmoney.mp4?alt=media&token=1a616261-fce9-4bba-a4d5-9bb9e3b00f57',
  'exploring': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fexploring.mp4?alt=media&token=da42411b-f64c-4cb3-9fec-b06cf2031290',
  'property': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fproperty.mp4?alt=media&token=0546d926-7714-48f9-a6f3-30f70e3cc8bb',
  'default': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fdefault.mp4?alt=media&token=0dd09a7e-f14d-4926-9489-734d2ea68a05'
};

export const Composition = ({
  meta,
  theme,
  transitions,
  timeline: originalTimeline,
  backgroundVideo,
  pexelsCredit,
  captionCadence = 1.2,
  captionStyle = 'general',
  musicTrack,
  generateAudio
}) => {
  const [fontsLoaded, setFontsLoaded] = useState(false);
  useEffect(() => {
    loadFonts();
    const t = setTimeout(() => setFontsLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const safeBackgroundVideo = backgroundVideo || FALLBACK_ASSETS[meta?.brand] || FALLBACK_ASSETS['default'];
  let currentStartFrame = 0;

  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  const baseVolume = generateAudio ? 0.25 : 0.8;
  const fadeOutDuration = 60;
  const dynamicVolume = interpolate(
    frame,
    [durationInFrames - fadeOutDuration, durationInFrames],
    [baseVolume, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const timeline = React.useMemo(() => {
    if (!originalTimeline) return [];
    return originalTimeline.map(item => {
      if (!item.captions || item.captions.length === 0) return item;
      const anchorFrame = item.captions[0].startFrame;
      // NOTE: Pre- instastarting for TikTok, the newStart was:
      //    const newStart = anchorFrame + Math.floor(relativeStart * captionCadence);
      const retimedCaptions = item.captions.map(cap => {
        const relativeStart = cap.startFrame - anchorFrame;
        const duration = cap.endFrame - cap.startFrame;

        const newStart = Math.floor(relativeStart * captionCadence);
        const newEnd = newStart + Math.floor(duration * captionCadence);
        return { ...cap, startFrame: newStart, endFrame: newEnd };
      });

      // INCISION 2: THE TAIL-END TRUNCATION
      // Since we universally purged the legacy "inhale" delay from the start of the clips,
      // we must subtract that exact amount of time from the total sequence duration 
      // so it cuts immediately after the audio finishes.
      const legacyDelay = item.audioDelay || 0;
      const adjustedDuration = Math.max(1, item.durationInFrames - legacyDelay);

      return { ...item, captions: retimedCaptions, durationInFrames: adjustedDuration };
    });
  }, [originalTimeline, captionCadence]);

  const teaserImages = timeline
    .filter(t => t.type === 'ranking_slide')
    .map(t => t.image || "")
    .slice(0, 4);

  const dynamicTheme = {
    ...theme,
    fontDisplay: "'Bebas Neue', sans-serif",
    fontMain: "'Fredoka', sans-serif",
    highlightAll: captionStyle === 'social',
    captionStyle: captionStyle
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      <FilmGrain opacity={0.06} />

      {musicTrack && (
        <Audio src={musicTrack} volume={dynamicVolume} loop />
      )}

      {timeline.map((item, index) => {
        const startFrame = currentStartFrame;
        const duration = item.durationInFrames + transitions.overlap;
        currentStartFrame += duration - transitions.overlap;
        const zIndex = index;

        return (
          <Sequence key={index} from={startFrame} durationInFrames={duration} style={{ zIndex }}>
            {index === 0 ? (
              // INCISION 1: ZERO-FRAME HOOK BYPASS
              // We strip the TransitionWrapper completely from the first clip to ensure 0ms latency.
              item.type === "intro" ? (
                <IntroSlide
                  item={item}
                  theme={dynamicTheme}
                  brand={meta?.brand}
                  teaserImages={teaserImages}
                  backgroundVideo={safeBackgroundVideo}
                />
              ) : (
                <RankingSlide
                  item={item}
                  theme={dynamicTheme}
                  duration={duration}
                  brand={meta?.brand}
                />
              )
            ) : (
              // STANDARD PIPELINE FOR SUBSEQUENT CLIPS
              <TransitionWrapper
                type={transitions.type}
                overlap={transitions.overlap}
                isFirst={false}
                theme={dynamicTheme}
              >
                {item.type === "ranking_slide" ? (
                  <RankingSlide
                    item={item}
                    theme={dynamicTheme}
                    duration={duration}
                    brand={meta?.brand}
                  />
                ) : item.type === "intro" ? (
                  <IntroSlide
                    item={item}
                    theme={dynamicTheme}
                    brand={meta?.brand}
                    teaserImages={teaserImages}
                    backgroundVideo={safeBackgroundVideo}
                  />
                ) : (
                  <OutroSlide
                    item={item}
                    theme={dynamicTheme}
                    brand={meta?.brand}
                    backgroundVideo={safeBackgroundVideo}
                    pexelsCredit={pexelsCredit}
                  />
                )}
              </TransitionWrapper>
            )}
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};